import { supabase } from './supabase';
import { ExchangeRates as ExchangeRatesType } from '@flows.cash/types';

// Make our local interface extend or match the imported type
interface ExchangeRates extends ExchangeRatesType {}

interface ExchangeRateResponse {
  base: string;
  rates: Record<string, number>;
  timestamp: number;
}

/**
 * Get the environment variable value
 * @param name Environment variable name
 * @param defaultValue Default value to use if environment variable is not set
 * @returns The value of the environment variable or default value
 */
function getEnvVar(name: string, defaultValue: string): string {
  // Try different environment variable patterns based on platform
  const value = typeof process !== 'undefined' 
    ? (
        // For Node.js
        process.env[name] || 
        // For Expo/React Native
        process.env[`EXPO_PUBLIC_${name}`] ||
        // For Next.js
        process.env[`NEXT_PUBLIC_${name}`]
      )
    : undefined;
    
  if (!value && defaultValue === '') {
    console.warn(`Missing required environment variable: ${name}`);
  }
  
  return value || defaultValue;
}

// Get the API key from environment variables
const OPEN_EXCHANGE_RATES_API_KEY = getEnvVar(
  'OPEN_EXCHANGE_RATES_API_KEY', 
  '1e25fc9b1cb944668a52c8bc5d71bb64' // Default fallback key - replace in production!
);

// API rate limiting
const API_RATE_LIMIT = {
  maxRequestsPerHour: 1000, // Adjust based on your plan
  requestCount: 0,
  lastResetTime: Date.now()
};

/**
 * Exchange rate service for fetching and converting currency rates
 */
export const exchangeRateService = {
  /**
   * Check if we're within API rate limits
   * @returns True if a request can be made, false if we'd exceed the rate limit
   */
  checkRateLimit(): boolean {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    
    // Reset counter if an hour has passed
    if (now - API_RATE_LIMIT.lastResetTime > oneHour) {
      API_RATE_LIMIT.requestCount = 0;
      API_RATE_LIMIT.lastResetTime = now;
    }
    
    return API_RATE_LIMIT.requestCount < API_RATE_LIMIT.maxRequestsPerHour;
  },

  /**
   * Fetch latest exchange rates, either from cache or from the API
   * @returns Exchange rates object or null if failed
   */
  async fetchLatestRates(): Promise<ExchangeRates | null> {
    try {
      // First try to load existing rates from database
      const existingRates = await this.getExistingRates();
      
      // If we found rates in the database, return them
      if (existingRates) {
        console.log('Using existing exchange rates from database');
        return existingRates;
      }
      
      // If no rates in database, fetch from API
      console.log('No existing rates found, fetching from API');
      return await this.fetchRatesDirectlyFromAPI();
    } catch (error) {
      console.error('Error in fetchLatestRates:', error);
      return null;
    }
  },

  /**
   * Only load existing exchange rates from the database without making API calls
   * @returns Exchange rates object or null if none exist
   */
  async getExistingRates(): Promise<ExchangeRates | null> {
    try {
      console.log('Loading existing exchange rates from database...');
      
      // Check if user is authenticated
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      
      if (!userId) {
        console.warn('No authenticated user found when loading exchange rates');
        return null;
      }
      
      // Get rates from the database
      const { data: dbRates, error } = await supabase
        .from('exchange_rates')
        .select('*')
        .eq('provider', 'openexchangerates')
        .order('updated_at', { ascending: false })
        .limit(1000); // Get the most recent records, limited to a reasonable number
      
      if (error) {
        console.error('Error fetching exchange rates from database:', error);
        return null;
      }
      
      if (!dbRates || dbRates.length === 0) {
        console.warn('No exchange rates found in database');
        return null;
      }
      
      console.log(`Loaded ${dbRates.length} exchange rates from database`);
      
      // Transform the database records into the expected format
      const ratesMap: Record<string, number> = {};
      let baseCurrency = 'USD'; // Default
      let latestTimestamp = 0;
      
      // Process each rate record
      dbRates.forEach(rate => {
        // Only process records with the correct base currency
        if (rate.base_currency === baseCurrency) {
          ratesMap[rate.target_currency] = rate.rate;
          
          // Track the latest timestamp
          const recordTimestamp = new Date(rate.updated_at).getTime();
          if (recordTimestamp > latestTimestamp) {
            latestTimestamp = recordTimestamp;
          }
        } else if (Object.keys(ratesMap).length === 0) {
          // If we haven't found any rates with our default base, use what we have
          baseCurrency = rate.base_currency;
          ratesMap[rate.target_currency] = rate.rate;
          
          const recordTimestamp = new Date(rate.updated_at).getTime();
          latestTimestamp = recordTimestamp;
        }
      });
      
      // If we found exchange rates, return them
      if (Object.keys(ratesMap).length > 0) {
        return {
          base: baseCurrency,
          rates: ratesMap,
          timestamp: latestTimestamp,
          source: 'cache'
        };
      }
      
      console.warn('Failed to construct exchange rates from database records');
      return null;
    } catch (error) {
      console.error('Error loading existing exchange rates:', error);
      return null;
    }
  },

  /**
   * Directly fetch exchange rates from the API, bypassing any caching logic
   * @returns Exchange rates object or null if failed
   */
  async fetchRatesDirectlyFromAPI(): Promise<ExchangeRates | null> {
    try {
      console.log('=== BEGIN fetchRatesDirectlyFromAPI ===');
      
      // Check if user is authenticated
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      
      console.log('Current user ID:', userId);
      
      if (!userId) {
        console.warn('No authenticated user found when fetching exchange rates');
      }
      
      // Check if we're within API rate limits
      if (!this.checkRateLimit()) {
        console.error('API rate limit exceeded, cannot fetch new rates');
        throw new Error('API rate limit exceeded');
      }
      
      // Fetch from the API
      console.log('Fetching exchange rates from API...');
      const apiUrl = `https://openexchangerates.org/api/latest.json?app_id=${OPEN_EXCHANGE_RATES_API_KEY}`;
      
      const response = await fetch(apiUrl);
      if (!response.ok) {
        console.error(`API response not OK: ${response.status} ${response.statusText}`);
        throw new Error(`Failed to fetch exchange rates from API: ${response.status} ${response.statusText}`);
      }
      
      const data: ExchangeRateResponse = await response.json();
      console.log(`API response received with ${Object.keys(data.rates).length} currency rates`);
      
      const exchangeRates: ExchangeRates = {
        base: data.base,
        rates: data.rates,
        timestamp: Date.now(),
        source: 'api'
      };

      // Store in Supabase
      try {
        console.log('Storing fresh exchange rates in Supabase...');
        
        // Check if user is authenticated before inserting
        if (!userId) {
          console.warn('Cannot store exchange rates: No authenticated user');
          return exchangeRates;
        }
        
        // First check if the table exists and is accessible
        console.log('Checking if exchange_rates table is accessible...');
        const { count: initialCount, error: countError } = await supabase
          .from('exchange_rates')
          .select('*', { count: 'exact', head: true });
          
        if (countError) {
          console.error('Error accessing exchange_rates table:', countError);
          throw countError;
        }
        
        console.log(`exchange_rates table is accessible, current count: ${initialCount}`);
        
        // We don't need to clear the table first, we can use upsert with the proper conflict handling
        console.log('Preparing to upsert exchange rates...');
        
        // The exchange_rates table has a different structure than what we're trying to insert
        // It expects individual currency pairs rather than a JSONB object with all rates
        const baseCurrency = exchangeRates.base; // Usually 'USD'
        const timestamp = new Date().toISOString();
        const provider = 'openexchangerates';
        
        console.log(`Preparing to upsert ${Object.keys(exchangeRates.rates).length} currency pairs with base ${baseCurrency}`);
        
        // Create an array of records to upsert
        const recordsToUpsert = Object.entries(exchangeRates.rates).map(([targetCurrency, rate]) => ({
          base_currency: baseCurrency,
          target_currency: targetCurrency,
          rate: rate,
          provider: provider,
          timestamp: timestamp,
          created_at: timestamp,
          updated_at: timestamp
        }));
        
        // Log a sample of what we're upserting
        console.log('Sample record:', JSON.stringify(recordsToUpsert[0], null, 2));
        
        // Upsert in batches to avoid hitting request size limits
        const BATCH_SIZE = 20;
        let successCount = 0;
        let failedBatches = 0;
        
        console.log(`Beginning batch upserts with batch size ${BATCH_SIZE}...`);
        
        for (let i = 0; i < recordsToUpsert.length; i += BATCH_SIZE) {
          const batch = recordsToUpsert.slice(i, Math.min(i + BATCH_SIZE, recordsToUpsert.length));
          
          console.log(`Processing batch ${i / BATCH_SIZE + 1} with ${batch.length} records...`);
          
          try {
            // First, try to delete existing records that match our criteria
            const deletePromises = batch.map(record => {
              return supabase
                .from('exchange_rates')
                .delete()
                .eq('base_currency', record.base_currency)
                .eq('target_currency', record.target_currency)
                .eq('provider', record.provider);
            });
            
            // Wait for all delete operations to complete
            await Promise.all(deletePromises);
            console.log(`Cleared existing records for batch ${i / BATCH_SIZE + 1}`);
            
            // Now insert the new records
            const { error: insertError } = await supabase
              .from('exchange_rates')
              .insert(batch);

            if (insertError) {
              console.error(`Error inserting batch ${i / BATCH_SIZE + 1}:`, insertError);
              console.error('Error details:', JSON.stringify(insertError, null, 2));
              failedBatches++;
            } else {
              // Insert was successful
              successCount += batch.length;
              console.log(`Successfully inserted batch ${i / BATCH_SIZE + 1}`);
            }
          } catch (batchError) {
            console.error(`Exception in batch ${i / BATCH_SIZE + 1}:`, batchError);
            failedBatches++;
          }
        }
        
        if (failedBatches > 0) {
          console.error(`Failed to upsert ${failedBatches} batches of exchange rates`);
        }
        
        console.log(`Successfully upserted ${successCount} exchange rates in database`);
        
        // Verify the upsert
        const { count, error: finalCountError } = await supabase
          .from('exchange_rates')
          .select('*', { count: 'exact', head: true });
          
        if (finalCountError) {
          console.error('Error verifying final count:', finalCountError);
        } else {
          console.log(`Verification: Found ${count} exchange rates in database`);
          
          if (count === 0) {
            console.error('Exchange rates were not upserted properly');
            throw new Error('Exchange rates were not upserted properly');
          }
        }
        
        // Return the fetched exchange rates regardless of storage success
        console.log('=== END fetchRatesDirectlyFromAPI ===');
        return exchangeRates;
      } catch (insertError) {
        console.error('Exception storing exchange rates:', insertError);
        console.error('Exception details:', JSON.stringify(insertError, null, 2));
        // Still return the fetched rates even if storage fails
      }
      
      return exchangeRates;
    } catch (error) {
      console.error('Error fetching exchange rates directly from API:', error);
      return null;
    }
  },

  /**
   * Fetch rates for specific currencies
   * @param currencies Optional array of currency codes to fetch
   * @returns Promise resolving to exchange rates
   */
  async fetchRates(currencies?: string[]): Promise<ExchangeRates | null> {
    try {
      // Log currencies if provided
      if (Array.isArray(currencies) && currencies.length > 0) {
        // Filter out invalid currency codes
        const validCurrencies = currencies.filter(code => 
          typeof code === 'string' && code.length === 3);
          
        console.log(`Fetching rates for ${validCurrencies.length} currencies: ${validCurrencies.join(',')}`);
      }
      
      // Currently we just fetch all rates regardless of requested currencies
      return await this.fetchLatestRates();
    } catch (error) {
      console.error('Error in fetchRates:', error);
      return null;
    }
  },

  /**
   * Convert an amount from one currency to another
   * @param amount Amount to convert
   * @param fromCurrency Source currency code
   * @param toCurrency Target currency code
   * @param rates Exchange rates object
   * @returns Converted amount
   */
  convertAmount(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    rates: Record<string, number>
  ): number {
    return this.convertCurrency(amount, fromCurrency, toCurrency, rates);
  },

  /**
   * Convert an amount from one currency to another using exchange rates
   * @param amount Amount to convert
   * @param fromCurrency Source currency code
   * @param toCurrency Target currency code
   * @param rates Exchange rates object
   * @returns Converted amount
   */
  convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    rates: Record<string, number>
  ): number {
    console.debug(`[ExchangeRatesService] Converting ${amount} from ${fromCurrency} to ${toCurrency}`);
    
    if (fromCurrency === toCurrency) {
      return amount;
    }
    
    // List of currencies that might not be supported by the exchange rate API
    const unsupportedCurrencies = ['IQD', 'IRR', 'SYP', 'VES'];
    
    // Check if we're dealing with unsupported currencies
    if (unsupportedCurrencies.includes(fromCurrency) || unsupportedCurrencies.includes(toCurrency)) {
      console.warn(`[ExchangeRatesService] Unsupported currency detected: ${
        unsupportedCurrencies.includes(fromCurrency) ? fromCurrency : toCurrency
      }`);
      
      // If we have a fallback rate for this currency pair, use it
      // This could be expanded to include more currency pairs as needed
      if (fromCurrency === 'USD' && toCurrency === 'IQD') {
        return Math.round((amount * 1309.5) * 10000) / 10000; // Approximate USD to IQD rate
      } else if (fromCurrency === 'IQD' && toCurrency === 'USD') {
        return Math.round((amount * 0.00076) * 10000) / 10000; // Approximate IQD to USD rate
      }
      
      // For other unsupported currencies, throw an error
      throw new Error(`Unsupported currency: ${
        unsupportedCurrencies.includes(fromCurrency) ? fromCurrency : toCurrency
      }`);
    }
    
    const fromRate = rates[fromCurrency];
    const toRate = rates[toCurrency];
    
    if (!fromRate || !toRate) {
      console.error(`[ExchangeRatesService] Invalid currency code: ${!fromRate ? fromCurrency : toCurrency}`);
      throw new Error(`Invalid currency code: ${!fromRate ? fromCurrency : toCurrency}`);
    }
    
    // Convert to USD first (base currency), then to target currency
    const convertedAmount = (amount / fromRate) * toRate;
    
    // Round to 4 decimal places
    return Math.round(convertedAmount * 10000) / 10000;
  },

  /**
   * Test function to manually insert a simple exchange rate record
   * @returns Promise resolving to success status
   */
  async testDatabaseInsertion(): Promise<boolean> {
    try {
      console.log('Testing exchange_rates table upsert...');
      
      // Check if user is authenticated
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        console.error('Authentication required: User must be logged in to insert exchange rates');
        return false;
      }
      
      const baseCurrency = 'USD';
      const timestamp = new Date().toISOString();
      const provider = 'test';
      
      // Create test records for a few currencies
      const testRecords = [
        {
          base_currency: baseCurrency,
          target_currency: 'USD',
          rate: 1,
          provider: provider,
          timestamp: timestamp,
          created_at: timestamp,
          updated_at: timestamp
        },
        {
          base_currency: baseCurrency,
          target_currency: 'EUR',
          rate: 0.85,
          provider: provider,
          timestamp: timestamp,
          created_at: timestamp,
          updated_at: timestamp
        },
        {
          base_currency: baseCurrency,
          target_currency: 'GBP',
          rate: 0.75,
          provider: provider,
          timestamp: timestamp,
          created_at: timestamp,
          updated_at: timestamp
        }
      ];
      
      console.log('Upserting test records:', testRecords);
      
      const { error } = await supabase
        .from('exchange_rates')
        .upsert(testRecords, {
          onConflict: 'base_currency,target_currency',
          ignoreDuplicates: false // Update existing records
        });
      
      if (error) {
        console.error('Test upsert failed:', error);
        console.error('Error details:', JSON.stringify(error));
        
        // Provide more specific error messages based on error code
        if (error.code === '42501') {
          console.error(`
            Row Level Security (RLS) policy violation detected.
            
            This likely means one of the following:
            1. Your user doesn't have permission to insert into the exchange_rates table
            2. The exchange_rates table has RLS policies that restrict insertions
            
            To fix this:
            - Check the RLS policies in your Supabase dashboard for the exchange_rates table
            - Ensure your user has the necessary permissions
            - Consider using a service role key for admin operations (server-side only)
            - Or create a serverless function with appropriate permissions
          `);
        }
        
        return false;
      }
      
      console.log('Test upsert successful!');
      return true;
    } catch (error) {
      console.error('Exception during test insertion:', error);
      console.error('Exception details:', JSON.stringify(error, null, 2));
      return false;
    }
  }
}; 